import { useUpdate } from "@/core/hooks/use-update.hook";
import { DatabaseRecord, TableRepo } from "@/core/repos/table.repo";
import { useSavedPaginations } from "@/core/stores/pagination.store";
import { TablePaginationConfig, TableColumnsType, TableProps, ConfigProvider, Table } from "antd";
import { createStyles } from "antd-style";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useState, useEffect, useCallback, Key } from "react";
import { useLocation } from "react-router";
import { useDebounce } from 'use-debounce';
// import { ColumnSearch, ColumnSearchPayload } from "./ColumnSearch";

const useStyle = createStyles(({ css }) => {
    return {
      customTable: css`
        .ant-table {
          .ant-table-container {
            .ant-table-body,
            .ant-table-content {
              scrollbar-width: thin;
              scrollbar-color: #eaeaea transparent;
              scrollbar-gutter: stable;
            }
          }
        }
      `,
    };
  });
export interface FilterSearchPayload {
    key: string,
    value: string
}
export interface TablePaginationData {current: number, pageSize: number};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type HeaderRenderFunction<T extends DatabaseRecord> = (text: any, record: T) => ReactNode;

export interface DataTableColumn<T extends DatabaseRecord> {
    name: string,
    dataIndex?: string,
    render?: HeaderRenderFunction<T>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pipe?:(value: any) => string|ReactNode,
    raw?: (record: T) => string,
    fixed?: "right"|"left",
    width?: number
}

interface DataTableProps<T extends DatabaseRecord> {
    columns: DataTableColumn<T>[],
    actions?: ReactNode[],
    repository: TableRepo<T>,
    filterMap: Map<string, FilterSearchPayload>,
    onSelect: (rows: T[]) => void,
    onPagination?: (current: number, pageSize: number) => void,
    hideNo?: boolean
    hideSelection?: boolean
    defaultParams?: object
    getTotal?: (total: number) => void
    summary?: () => ReactNode
}
export type DataTable<T extends DatabaseRecord> = React.FC<DataTableProps<T>>
export const DataTable = <T extends DatabaseRecord,>(props: DataTableProps<T>) => {
    const {
        columns,
        repository,
        filterMap,
        onSelect,
        defaultParams,
        summary,
        onPagination
    } = props;
    const paginationStore = useSavedPaginations();
    const {pathname} = useLocation();
    const [data, setData] = useState<T[]>([])
    const changes = repository.useChanges();
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: paginationStore.savedPagination.get(pathname)??1,
        pageSize: 10,
        showSizeChanger: true
    })
    const [selectedRowKeys, setSelectedRowsKeys] = useState<Key[]>();
    const [debouncedFilterMap] = useDebounce(filterMap, 200);
    // const columnSearchMap: Map<string, ColumnSearchPayload> = new Map();

    const tableColumns: TableColumnsType = [ ];

    if (!props.hideNo) {
        tableColumns.push({
            title: <div className="p-2 text-center">STT</div>,
            key: 'key',
            dataIndex: 'key',
            fixed: 'left',
            render: (value) => (<div className="px-4 py-3 text-center">{value}</div>)
            // children: [{
            //     title: <div className="h-8 table-row-dark"></div>,
            //     dataIndex: 'key',
            //     render: (value) => (<div className="px-1 py-3 text-center">{value}</div>)
            // }]
        })
    }

    function getRowKey(index: number) {
        const page = (pagination.current??1) - 1;
        const size = pagination.pageSize??10;
        return page*size + index + 1
    }

    async function fetchData() {
        setLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let query: any = {};
        filterMap.forEach(filter => {
            if (filter.value !== undefined&&filter.value !== null&&filter.value !== "") {
                query[filter.key] = filter.value
            }
        })
        if (defaultParams) {
            query = {...query, ...defaultParams};
        }
        const data = await repository.getData(pagination.current??1, query, pagination.pageSize);
        if (data) {
            //Prevent pagination index overflow
            if (data.total > 0&&pagination.current&&pagination.current > Math.ceil(data.total/(pagination.pageSize??10))) {
                setPagination({
                    ...pagination,
                    current: 1,
                    total: data.total
                })
                if (onPagination&&pagination.pageSize) onPagination(1, pagination.pageSize);

            } else {
                setPagination({
                    ...pagination,
                    total: data.total
                })
                if (onPagination&&pagination.current&&pagination.pageSize) onPagination(pagination.current, pagination.pageSize);
                data.data.forEach((row, index) => {
                    row.key = getRowKey(index).toString();
                });
                setData(data.data);
                if (props.getTotal) {
                    props.getTotal(data.total);
                }
            } 
        }
        setLoading(false);
    }

    useUpdate(()=>{
        fetchData();
        paginationStore.setPagePagination(pathname, pagination?.current??1);
    }, [
        pagination?.current,
        pagination?.pageSize,
    ]);

    useUpdate(()=>{
        fetchData();
        //Reset selection after changes occured
        setSelectedRowsKeys([]);
        onSelect([]);
    }, [changes])

    const handleTableChange: TableProps<T>['onChange'] = (pagination) => {
        setPagination({
          ...pagination
        });
    };

    // function onColumnSearch(event: ColumnSearchPayload) {
    //     columnSearchMap.set(event.dataIndex, event);
    //     console.log(columnSearchMap);
    // }

    //Fetch new data on query changes
    useEffect(()=>{
        fetchData();
        setSelectedRowsKeys([]);
        onSelect([]);
    }, [ debouncedFilterMap ])

    function buildColumn(column: DataTableColumn<T>) {
        return {
            title: <div className="px-4 py-6">{column.name}</div>,
            key: column.dataIndex,
            dataIndex: column.dataIndex,
            fixed: column.fixed,
            render: column.render? column.render: (value: string) => <div className="px-4 flex flex-col justify-center h-[60px]">{column.pipe?column.pipe(value):value}</div>,
            width: column.width
            // children: [{
            //     title:dataIndex
            //         ?<div className="h-8 table-row-dark">
            //             <ColumnSearch onChange={onColumnSearch} dataIndex={dataIndex}/>
            //         </div>
            //         :<div className="h-8 table-row-dark"></div>,
            //     dataIndex: dataIndex,
            //     render: render? render: (value: string) => <div className="px-2 py-1">{value}</div>
            // }],
        }
    }

    columns.forEach(column => {
        tableColumns.push(buildColumn(column));
    })
    const { styles } = useStyle();
    return (
        <ConfigProvider theme={{
            components: {
                Table: {
                    headerBg: "var(--primary-color)",
                    headerSplitColor: "white",
                    headerColor: "white",
                    cellPaddingBlock: 0,
                    cellPaddingInline: 0,
                    headerBorderRadius: 0,
                    cellFontSize: 14,
                    fontWeightStrong: 600,
                    lineWidth: 1.5
                },
                Input: {
                    inputFontSize: 14,
                    paddingBlock: 6
                }
            },
            token: {
                colorPrimary: "#0B7DB6",
            }
        }}>
            <Table
                rowHoverable={false}
                dataSource={data}
                rowKey={(record: T) => record.id}
                columns={tableColumns} bordered={true}
                rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light': 'table-row-dark'}
                rowSelection={props.hideSelection?undefined:{
                    type: 'checkbox',
                    selectedRowKeys: selectedRowKeys,
                    preserveSelectedRowKeys: true,
                    onChange(selectedRowKeys, selectedRows) {
                        setSelectedRowsKeys(selectedRowKeys);
                        onSelect(selectedRows);
                    },
                }}
                pagination={pagination}
                loading={loading}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
                className={styles.customTable}
                summary={summary}
            />
        </ConfigProvider>
    )
}